// 화장장 견적 퍼널(template=crematorium) 상수.
// 무빈소 퍼널(mubinso) 구조를 복제하되, 분기 질문 → 종료 화면 / 정보 퍼널 흐름으로 재구성.
// 진입: /match/request/?template=crematorium

/** 컨테이너 내부 스크린 상태(라우트 없이 상태머신으로만 전환). */
export type CrematoriumScreen =
  | "q1"
  | "q2_urgent"
  | "q2_advance"
  | "terminal_death"
  | "terminal_reloc"
  | "q3"
  | "funnel"
  | "guide";

// ---------------------------------------------------------------------------
// Q1 - 화장장 문의 시점
// ---------------------------------------------------------------------------
export const CREMA_Q1_TITLE = "현재 화장장 예약이 필요하신가요?";

export const CREMA_Q1_OPTIONS = [
  { label: "당장 화장장 예약이 필요해요", event: "q1-answer" as const, next: "q2_urgent" as const },
  { label: "미리 알아보는 중이에요", event: "q1-answer" as const, next: "q2_advance" as const },
] as const;

// ---------------------------------------------------------------------------
// Q2 (urgent) - 당장 필요한 상황
// ---------------------------------------------------------------------------
export const CREMA_Q2_URGENT_TITLE = "어떤 상황이신가요?";

export const CREMA_Q2_URGENT_OPTIONS = [
  {
    label: "얼마 전 임종하신 상태입니다.",
    event: "q2u-answer" as const,
    next: "terminal_death" as const,
  },
  { label: "개장/이장을 해야 해요", event: "q2u-answer" as const, next: "terminal_reloc" as const },
  { label: "화장 예약을 미리 하고 싶어요", event: "q2u-answer" as const, next: "q3" as const },
] as const;

// ---------------------------------------------------------------------------
// Q2 (advance) - 미리 알아보는 상황
// ---------------------------------------------------------------------------
export const CREMA_Q2_ADVANCE_TITLE = "어떤 상황이신가요?";

export const CREMA_Q2_ADVANCE_OPTIONS = [
  { label: "임종하신 상태입니다.", event: "q2a-answer" as const, next: "terminal_death" as const },
  { label: "개장/이장을 해야 해요", event: "q2a-answer" as const, next: "terminal_reloc" as const },
  { label: "임종이 며칠 남지 않았어요.", event: "q2a-answer" as const, next: "q3" as const },
  {
    label: "1주에서 한 달 정도 기간이 남은 것 같아요.",
    event: "q2a-answer" as const,
    next: "q3" as const,
  },
  {
    label: "급하지 않지만 미리 알아보려고 해요.",
    event: "q2a-answer" as const,
    next: "q3" as const,
  },
] as const;

// ---------------------------------------------------------------------------
// Q3 - 궁금한 점
// ---------------------------------------------------------------------------
export const CREMA_Q3_TITLE = "어떤 점이 궁금하신가요?";

export const CREMA_Q3_OPTIONS = [
  { label: "화장장 예약 절차가 궁금해요", event: "q3-answer" as const, next: "funnel" as const },
  { label: "화장장 비용이 궁금해요", event: "q3-answer" as const, next: "funnel" as const },
  { label: "화장장 비용 절약법이 궁금해요", event: "q3-answer" as const, next: "funnel" as const },
  { label: "세 가지 다 궁금해요", event: "q3-answer" as const, next: "funnel" as const },
] as const;

// ---------------------------------------------------------------------------
// 종료 화면 카피
// ---------------------------------------------------------------------------
export const CREMA_TERMINAL_DEATH = {
  headline: "임종하셨다면,\n빠르게 화장예약을\n하는 게 중요해요.",
  highlightText: "빠르게 화장예약",
  callout:
    "화장 예약이 밀리는 경우, <b>4일장 ~ 5일장까지 장례 기간이 늘어나요.</b> 그에 따라 빈소대여료 등 추가 비용이 발생될 수 있어요.",
  checks: [
    "고이장례연구소에서 빠르게 화장 예약을 도와드려요.",
    "화장 예약이 밀려도 비용을 아낄 수 있게 도와드릴 수 있어요.",
    "장례에 필요한 어떤 도움이든 드릴 수 있어요.",
  ],
  cta: "임종 화장예약 전화하기",
} as const;

export const CREMA_TERMINAL_RELOC = {
  headline: "개장 이장 관련\n안내드려요.",
  steps: [
    {
      title: "관할 지자체에 개장 신고",
      desc: "매장 묘지가 있는 지자체에\n개장(이장) 신고서를 제출해야 해요.",
    },
    {
      title: "화장장 예약 및 진행",
      desc: "개장 허가 후 화장장을 예약하고\n절차를 진행할 수 있어요.\n자세한 사항은 전화로 안내드릴게요.",
    },
  ],
  cta: "전화하기",
} as const;

// ---------------------------------------------------------------------------
// 정보 퍼널 카피
// ---------------------------------------------------------------------------
export const CREMA_FUNNEL_STEP1 = {
  headline: "화장장 예약 대란에\n주의하셔야해요.",
  highlightText: "화장장 예약 대란",
  body: "최근 사망자 수 증가로 전국 화장장이 포화 상태예요.\n원하는 날짜에 예약하지 못하면 4일장, 5일장으로 이어질 수 있어요.\n",
  callout:
    "<b>원치않는 4일 장례, 공급 늘리는 대안 필요하다 / 중부일보 26.10.18</b>\n\n`화장시설의 극심한 수급 불균형으로 유족들이 <b>원치 않는 4일장을 치러야 하는 고통스러운 현실이 반복</b>되고 있다. 최근 경기도 일대 장례식장에서는 3일장에 맞춰 화장을 진행하지 못하고 장례 일정을 강제로 연장하는 사례가 잇따르고 있다는 얘기다.` \n",
} as const;

export const CREMA_FUNNEL_STEP2 = {
  headline: "화장 일정으로 4일장이 되면\n추가 비용이 발생해요.",
  highlightText: "추가 비용",
  body: "화장 예약을 제때 하지 못하면 장례 기간이 길어지고,\n장례식장 빈소 이용료, 안치료 등 추가 비용이 발생해요.",
  image: "platform/crema_cost_img.png",
  imageAlt: "4일장 추가 비용 안내",
} as const;

export const CREMA_FUNNEL_STEP3 = {
  headline: "화장 예약은 장례지도사가\n 가장 중요해요.",
  highlightText: "장례지도사",
  body: "장례지도사는 화장 예약에 익숙하고, \n전용 화장 예약 시스템으로 빠르게 예약이 가능해요.",
  checks: [
    "장례식장에서 장례를 진행하실 경우, 상주마다 장례지도사가 있지 않아서, 화장 예약이 밀릴 수 있어요,",
    "화장이 많은 시기에, 상주분이 직접 화장 예약을 하는 것도 어려움이 많아요.",
  ],
  callout:
    "<b>상조를 이용할 경우, 1:1로 장례지도사가 전담</b>되어 화장 예약이 밀리는 경우가 적어요.",
} as const;

export const CREMA_CONVERSION = {
  headline: "화장장 비용 및 절차와\n화장이 밀릴 경우 대응법까지\n고이에서 한번에 안내해드려요",
  highlightText: "고이에서 한번에 안내해드려요",
  sub: "누적 장례 5,000건 이상의 경험을 바탕으로\n내 상황에 맞는 화장장 정보를 정리해서 알려드려요.",
  checkItems: [
    { text: "내 예상 지역의 화장 예약 정보 안내" },
    { text: "화장 비용을 포함한, 내 지역의 총 장례 비용" },
    { text: "꼭 필요한 항목과 추가 비용 발생 가능 항목" },
  ],
  reviewTitle: "이용 후기",
  reviews: [
    {
      text: "화장 예약이 다 찼다고 해서 걱정했는데, 지도사님이 바로 잡아주셔서 3일장으로 마칠 수 있었어요.",
      meta: "2026년 7월 후기",
    },
    {
      text: "관외 비용이 이렇게 차이 나는 줄 몰랐어요. 미리 안내받아서 비용을 많이 줄였습니다.",
      meta: "2026년 5월 후기",
    },
  ],
  primaryLabel: "예상 비용 무료로 한 번에 확인하기",
} as const;

// ---------------------------------------------------------------------------
// 가이드 화면 카피
// ---------------------------------------------------------------------------
export const CREMA_GUIDE = {
  headline: "신청이 접수되었어요.",
  sub: "장례지도사가 곧 연락드릴 거예요.\n급하시면 바로 전화로 안내해드릴게요.",
  cta: "전화 연결하기",
} as const;

export const CREMA_PHONE = "1666-9784";
